/**
 * The people who sell for the agency, what they have earned, and what they have been paid.
 *
 * **Nothing on this screen works out a money figure.** Every number below arrives from
 * `marketerLedger()` in `apps/api/src/routes/payouts.ts`, which is the single implementation of
 * "what does this agency owe this person" — ADR-0115, applying ADR-0099, which is the record of
 * what happened the last time the same money was worked out in two places and the two answers
 * differed by 942,498,950 minor units. So the list shows the server's `owedLabel`, the payment box
 * is capped at the server's `owed`, and the statement prints the server's own lines. There is no
 * arithmetic in this file beyond deciding whether a figure is greater than zero.
 *
 * **A marketer is either a colleague or an outsider, never both.** A colleague already signs in and
 * is picked from the team; an outsider is a name and an address, gets a `parties` row carrying the
 * `marketer` role, and is invited to the portal — **never to a membership**. That line is the API's
 * to hold and it holds it; what this screen must not do is offer a control that suggests otherwise,
 * which is why "invite" is worded as an invitation to see their own statement rather than as an
 * invitation to join the agency.
 *
 * **Marking somebody inactive is the ordinary end, not deleting them.** A sale closed three years
 * ago goes on paying commission for as long as the buyer is paying, so a statement has to be
 * producible for every marketer who ever sold anything. The API refuses a deletion while they are
 * named on a live sale and says so in a sentence; that refusal is shown as it arrives.
 */

import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { COMMISSION_BASIS_INFO } from "@rentorng/core";
import { api } from "@/lib/api";
import { formValues, useApiMutation } from "@/lib/mutations";
import { minor, useFormat } from "@/lib/format";
import { useSession } from "@/lib/session";
import { commissionLabel } from "@/lib/development";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardTitle,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Modal,
  PageHeader,
  Select,
  Spinner,
  Table,
} from "@/components/ui";
import { MoneyInput } from "@/components/money-input";
import { Refusal } from "@/components/padlock";
import { Copyright } from "@/components/copyright";
import { AgencyMark } from "@/components/agency-mark";

/* --------------------------------------------------------------- the shapes */

/** As the server describes one, with the ledger's three figures already on it. */
interface MarketerRow {
  id: string;
  name: string;
  email: string | null;
  status: string;
  /** Exactly one of these two is set. It is what makes them a colleague or an outsider. */
  userId: string | null;
  partyId: string | null;
  earned: number;
  earnedLabel: string;
  paid: number;
  paidLabel: string;
  owed: number;
  owedLabel: string;
  saleCount: number;
}

interface MarketerSale {
  purchaseId: string;
  reference: string;
  buyerName: string;
  estateName: string;
  plotLabel: string | null;
  price: number;
  priceLabel: string;
  commissionBp: number | null;
  basis: string;
  basisLabel: string;
  status: string;
  /** What the sale earns in total, and what it has earned so far. Both from the ledger. */
  full: number;
  fullLabel: string;
  earned: number;
  earnedLabel: string;
}

interface StatementLine {
  purchaseId: string;
  purchaseReference: string;
  /** Which table `sourceId` is in, so a figure can be opened and checked rather than believed. */
  sourceKind: "allocation" | "sale";
  sourceId: string;
  buyerName: string;
  estateName: string;
  plotLabel: string | null;
  reference: string;
  date: string;
  description: string;
  gross: number;
  grossLabel: string;
  bp: number;
  commission: number;
  commissionLabel: string;
}

interface MarketerStatement {
  earned: number;
  earnedLabel: string;
  paid: number;
  paidLabel: string;
  owed: number;
  owedLabel: string;
  saleCount: number;
  lineCount: number;
  lines: StatementLine[];
}

/**
 * A figure the statement's own lines do not agree with.
 *
 * Empty on every healthy account, and it is drawn loudly when it is not. `reconcileMarketer()`
 * checks the statement against itself on the way out — the same check that runs before a payment is
 * allowed — because a wrong figure nobody handed over is a bug and a wrong figure somebody handed
 * over is an argument.
 */
interface Problem {
  what: string;
  expected: number;
  found: number;
}

interface Agency {
  name: string;
  phone: string | null;
  email: string | null;
  address: string | null;
  logoUrl: string | null;
}

const STATUS_TONE: Record<string, "success" | "warn" | "muted"> = {
  active: "success",
  invited: "warn",
  inactive: "muted",
};

/**
 * "Invited" reads as a state, not as an instruction.
 *
 * The three are `MARKETER_STATUSES` in `@rentorng/db`, which the browser deliberately does not
 * depend on — the application talks to the API, not to the schema. Written out here rather than
 * imported, and the filter below is built from this object so the two cannot disagree about how
 * many there are.
 */
const STATUS_LABEL: Record<string, string> = {
  active: "Active",
  invited: "Invited",
  inactive: "Inactive",
};

/* ------------------------------------------------------------------ the list */

export function MarketersPage() {
  const { can } = useSession();
  const fmt = useFormat();
  const [adding, setAdding] = useState(false);
  const [status, setStatus] = useState("");

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["marketers", status],
    queryFn: () =>
      api.get<{ marketers: MarketerRow[]; outstanding: number }>(
        `/marketers${status ? `?status=${status}` : ""}`,
      ),
  });

  const owed = data?.outstanding ?? 0;

  return (
    <div>
      <PageHeader
        title="Marketers"
        back={{ to: "/app/development", label: "Development" }}
        subtitle={
          data
            ? `${data.marketers.length} ${data.marketers.length === 1 ? "person" : "people"} · ${fmt.money(owed)} outstanding`
            : undefined
        }
        description="Everybody who sells for you, on commission, and what each is owed. Commission accrues as buyers pay unless the property says otherwise."
        actions={
          can("party.edit") ? <Button onClick={() => setAdding(true)}>Add a marketer</Button> : null
        }
      />

      <Card padded={false}>
        <div className="flex flex-wrap items-center gap-2 border-b border-ink-200 p-3">
          <Select
            aria-label="Filter marketers"
            className="w-48"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            <option value="">Everybody</option>
            {Object.entries(STATUS_LABEL).map(([code, label]) => (
              <option key={code} value={code}>
                {label}
              </option>
            ))}
          </Select>
        </div>

        {isLoading ? (
          <Spinner />
        ) : error ? (
          <ErrorState error={error} retry={() => refetch()} />
        ) : !data?.marketers.length ? (
          <EmptyState
            title={status ? "Nobody matched that." : "No marketers recorded."}
            hint="A marketer is somebody who sells for you on commission — a colleague, or somebody outside the agency. The rate is set on the property and inherited by everything inside it."
            action={can("party.edit") ? <Button onClick={() => setAdding(true)}>Add a marketer</Button> : null}
          />
        ) : (
          <Table
            head={
              <tr>
                <th>Name</th>
                <th>On your team</th>
                <th className="num">Sales</th>
                <th className="num">Earned</th>
                <th className="num">Paid</th>
                <th className="num">Owed</th>
                <th>Status</th>
              </tr>
            }
          >
            {data.marketers.map((marketer) => (
              <tr key={marketer.id} data-marketer={marketer.name}>
                <td>
                  <Link
                    className="font-medium text-brand-700 hover:underline"
                    to={`/app/development/marketers/${marketer.id}`}
                  >
                    {marketer.name}
                  </Link>
                  {marketer.email ? <div className="text-xs text-ink-500">{marketer.email}</div> : null}
                </td>
                <td className="text-ink-600">{marketer.userId ? "Yes" : "No"}</td>
                <td className="num">{marketer.saleCount}</td>
                <td className="num">{marketer.earnedLabel}</td>
                <td className="num text-ink-600">{marketer.paidLabel}</td>
                {/*
                  Owed is allowed to be negative and is shown as it arrives.

                  Somebody paid ahead of a sale that was then cancelled owes the agency, and
                  clamping the figure at zero would lose that debt the moment it appeared. The
                  server does not clamp it; nor does this.
                */}
                <td className={`num font-medium ${marketer.owed < 0 ? "text-danger-600" : ""}`}>
                  {marketer.owedLabel}
                </td>
                <td>
                  <Badge tone={STATUS_TONE[marketer.status] ?? "muted"}>
                    {STATUS_LABEL[marketer.status] ?? marketer.status}
                  </Badge>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      {adding ? <AddMarketer onClose={() => setAdding(false)} /> : null}
    </div>
  );
}

/**
 * Recording somebody, in the two shapes the API accepts and no third.
 *
 * A colleague is chosen from the team and everything else about them is taken from their own
 * record — a name and an address typed again here would be a second identity for one person. An
 * outsider is a name, and an address is what makes them reachable at all.
 *
 * **The team list is allowed to fail.** Reading it needs `org.members`, which somebody who may
 * record a marketer does not necessarily hold; when it is refused the colleague option is simply
 * not offered, which is a better outcome than a dialog that will not open.
 */
function AddMarketer({ onClose }: { onClose: () => void }) {
  const [kind, setKind] = useState<"team" | "outside">("outside");

  const { data: team } = useQuery({
    queryKey: ["team"],
    queryFn: () => api.get<{ staff: Array<{ id: string; name: string; email: string }> }>("/settings/team"),
    retry: false,
  });

  const create = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.post<{ id: string }>("/marketers", input),
    invalidates: [["marketers"]],
    onSuccess: () => onClose(),
  });

  const colleagues = team?.staff ?? [];

  return (
    <Modal open onClose={onClose} title="Add a marketer">
      {create.errorMessage ? (
        <div className="mb-4">
          <Refusal error={create.error} message={create.errorMessage} />
        </div>
      ) : null}

      <form
        className="grid gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          const values = formValues(event.currentTarget);
          /*
            One shape or the other, never both keys at once.

            The API refuses a body carrying `userId` beside `email`, and the CHECK on the table says
            the same thing underneath it. Sending only the half that applies means the refusal is
            about something the person actually did rather than about a field they never saw.
          */
          create.mutate(
            kind === "team"
              ? { userId: values.userId }
              : {
                  name: values.name,
                  email: values.email || null,
                  phone: values.phone || null,
                  invite: Boolean(values.email),
                },
          );
        }}
      >
        <Field label="Who is this" htmlFor="kind" hint="A colleague already signs in. Somebody outside the agency is invited to see their own statement.">
          <Select id="kind" value={kind} onChange={(event) => setKind(event.target.value as "team" | "outside")}>
            <option value="outside">Somebody outside the agency</option>
            <option value="team" disabled={!colleagues.length}>
              {colleagues.length ? "Somebody on your team" : "Somebody on your team — the team list is not available to you"}
            </option>
          </Select>
        </Field>

        {kind === "team" ? (
          <Field label="Team member" htmlFor="userId" required>
            <Select id="userId" name="userId" required defaultValue="">
              <option value="">Choose a colleague…</option>
              {colleagues.map((member) => (
                <option key={member.id} value={member.id}>
                  {member.name} — {member.email}
                </option>
              ))}
            </Select>
          </Field>
        ) : (
          <>
            <Field label="Name" htmlFor="name" required>
              <Input id="name" name="name" required placeholder="Adaeze Nwosu" />
            </Field>
            <Field
              label="Email address"
              htmlFor="email"
              hint="An invitation is sent so they can see their own statement. They never get access to your agency's records."
            >
              <Input id="email" name="email" type="email" placeholder="adaeze@example.com" />
            </Field>
            <Field label="Phone" htmlFor="phone">
              <Input id="phone" name="phone" placeholder="+234 800 000 0000" />
            </Field>
          </>
        )}

        <div className="flex flex-wrap gap-2">
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? "Saving…" : "Add the marketer"}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/* ---------------------------------------------------------------- one person */

export function MarketerDetailPage() {
  const { id = "" } = useParams();
  const { can } = useSession();
  const [paying, setPaying] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["marketer", id],
    queryFn: () =>
      api.get<{
        marketer: MarketerRow;
        sales: MarketerSale[];
        statement: MarketerStatement;
        problems: Problem[];
      }>(`/marketers/${id}`),
  });

  /*
    The payments, from the statement endpoint.

    `GET /marketers/:id` carries the sales and the ledger; the payments — including the withdrawn
    ones, which the arithmetic deliberately leaves out — are on the statement. Two requests rather
    than one because the second is also what the printable page reads, and asking the same question
    twice of one function is exactly the shape ADR-0099 asks for.
  */
  const paid = useQuery({
    queryKey: ["marketer-statement", id],
    queryFn: () => api.get<{ payments: Payment[] }>(`/marketers/${id}/statement`),
  });

  const invalidates = [["marketer", id], ["marketer-statement", id], ["marketers"]] as string[][];

  const pay = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.post(`/marketers/${id}/payments`, input),
    invalidates,
    onSuccess: () => setPaying(false),
  });

  const reverse = useApiMutation({
    mutationFn: ({ paymentId, reason }: { paymentId: string; reason: string }) =>
      api.post(`/marketer-payments/${paymentId}/reverse`, { reason }),
    invalidates,
  });

  const setStatus = useApiMutation({
    mutationFn: (status: string) => api.patch(`/marketers/${id}`, { status }),
    invalidates,
  });

  const invite = useApiMutation({
    mutationFn: () => api.post(`/marketers/${id}/invite`, {}),
    invalidates,
  });

  if (isLoading) return <Spinner />;
  if (error || !data) return <ErrorState error={error} retry={() => refetch()} />;

  const { marketer, statement, sales, problems } = data;
  const failure = pay.error ?? reverse.error ?? setStatus.error ?? invite.error;
  const failureMessage = pay.errorMessage ?? reverse.errorMessage ?? setStatus.errorMessage ?? invite.errorMessage;

  return (
    <div>
      <PageHeader
        title={marketer.name}
        back={{ to: "/app/development/marketers", label: "Marketers" }}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <Badge tone={STATUS_TONE[marketer.status] ?? "muted"}>
              {STATUS_LABEL[marketer.status] ?? marketer.status}
            </Badge>
            <span>{marketer.userId ? "On your team" : "Outside the agency"}</span>
            {marketer.email ? <span>· {marketer.email}</span> : null}
          </span>
        }
        description="Everything earned, less everything paid. The account is cumulative and takes no period."
        actions={
          <a className="btn btn-secondary btn-sm" href={`/app/development/marketers/${id}/statement`}>
            Statement
          </a>
        }
      />

      {failureMessage ? (
        <div className="mb-4">
          <Refusal error={failure} message={failureMessage} />
        </div>
      ) : null}

      {/*
        A statement that does not add up, said before anything else on the screen.

        Empty on every healthy account. When it is not, the API will refuse to pay from it — which
        is the right refusal and a baffling one to meet at the moment of pressing a button, so it is
        said here instead.
      */}
      {problems.length ? (
        <div className="mb-6">
          <Alert tone="danger" title="This account does not reconcile.">
            <ul className="mt-1 space-y-1" data-problems>
              {problems.map((problem) => (
                <li key={problem.what}>
                  {problem.what} came to {problem.found} where the lines give {problem.expected}.
                </li>
              ))}
            </ul>
            <p className="mt-2 text-xs">Nothing can be paid from this account until it agrees with its own lines.</p>
          </Alert>
        </div>
      ) : null}

      <div className="mb-6 grid gap-3 sm:grid-cols-3">
        <Card>
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Earned</p>
          <p className="mt-1 text-2xl font-semibold">{statement.earnedLabel}</p>
          <p className="text-xs text-ink-500">
            over {statement.lineCount} {statement.lineCount === 1 ? "entry" : "entries"}
          </p>
        </Card>
        <Card>
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Paid</p>
          <p className="mt-1 text-2xl font-semibold">{statement.paidLabel}</p>
        </Card>
        <Card>
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Owed</p>
          <p className={`mt-1 text-2xl font-semibold ${statement.owed < 0 ? "text-danger-600" : ""}`} data-owed>
            {statement.owedLabel}
          </p>
          {statement.owed < 0 ? (
            <p className="text-xs text-danger-600">Paid ahead of what has been earned.</p>
          ) : null}
        </Card>
      </div>

      {can("payout.record") || can("party.edit") ? (
        <Card className="mb-6">
          <CardTitle>Actions</CardTitle>
          <div className="flex flex-wrap gap-2">
            {can("payout.record") ? (
              <Button onClick={() => setPaying((open) => !open)} disabled={statement.owed <= 0 && !paying}>
                {paying ? "Cancel" : "Record a payment"}
              </Button>
            ) : null}
            {/* Only for somebody outside the agency. A colleague already signs in and there is
                nothing to invite them to — the API says exactly that, and offering the button
                anyway would be offering a refusal. */}
            {can("party.edit") && !marketer.userId && marketer.email ? (
              <Button variant="secondary" disabled={invite.isPending} onClick={() => invite.mutate(undefined as never)}>
                {invite.isPending ? "Sending…" : marketer.status === "invited" ? "Send the invitation again" : "Invite them"}
              </Button>
            ) : null}
            {can("party.edit") ? (
              <Button
                variant="secondary"
                disabled={setStatus.isPending}
                onClick={() => setStatus.mutate(marketer.status === "inactive" ? "active" : "inactive")}
              >
                {marketer.status === "inactive" ? "Mark active" : "Mark inactive"}
              </Button>
            ) : null}
          </div>

          {statement.owed <= 0 && can("payout.record") ? (
            <p className="mt-3 text-xs text-ink-500">
              Nothing is owed at the moment. Commission accrues as buyers pay, so there may be nothing yet on a sale
              that has been made.
            </p>
          ) : null}

          {paying ? (
            <form
              className="mt-4 grid gap-4 border-t border-ink-100 pt-4 sm:grid-cols-4"
              onSubmit={(event) => {
                event.preventDefault();
                const values = formValues(event.currentTarget);
                pay.mutate({
                  amount: minor(String(values.amount ?? "")) ?? 0,
                  paidOn: values.paidOn,
                  method: values.method,
                  paymentReference: values.paymentReference || null,
                  notes: values.notes || null,
                });
              }}
            >
              {/*
                Capped at what is owed, and the API refuses past it anyway.

                The box carries the ceiling so somebody typing a figure is stopped while they type
                rather than after they press; the server's guard is the one that decides, because it
                recomputes the ledger at that moment and this number is already a second old.
              */}
              <Field
                label="Amount"
                htmlFor="amount"
                required
                hint={`At most ${statement.owedLabel}, which is what the account stands at.`}
              >
                <MoneyInput id="amount" name="amount" required placeholder={String(statement.owed / 100)} />
              </Field>
              <Field label="Date paid" htmlFor="paidOn" required>
                <Input
                  id="paidOn"
                  name="paidOn"
                  type="date"
                  required
                  defaultValue={new Date().toISOString().slice(0, 10)}
                />
              </Field>
              <Field label="Method" htmlFor="method">
                <Select id="method" name="method" defaultValue="bank_transfer">
                  <option value="bank_transfer">Bank transfer</option>
                  <option value="cash">Cash</option>
                  <option value="cheque">Cheque</option>
                  <option value="pos">POS</option>
                  <option value="mobile_money">Mobile money</option>
                </Select>
              </Field>
              <Field label="Reference" htmlFor="paymentReference" hint="The transfer narration, so it can be found again.">
                <Input id="paymentReference" name="paymentReference" placeholder="Transfer narration" />
              </Field>
              <div className="sm:col-span-4">
                <Button type="submit" disabled={pay.isPending}>
                  {pay.isPending ? "Recording…" : "Record the payment"}
                </Button>
                <p className="mt-2 text-xs text-ink-500">
                  Marked by hand, because the transfer is made outside this system.
                </p>
              </div>
            </form>
          ) : null}
        </Card>
      ) : null}

      {/* ------------------------------------------------------------ the sales */}

      <Card padded={false} className="mb-6">
        <div className="border-b border-ink-200 p-4">
          <CardTitle hint="The rate and the basis on each of these were frozen at the moment of sale. Re-rating a property moves what the next sale earns and cannot reach one already done.">
            Sales
          </CardTitle>
        </div>
        {!sales.length ? (
          <p className="p-6 text-center text-sm text-ink-500">Not on any sale yet.</p>
        ) : (
          <Table
            head={
              <tr>
                <th>Sale</th>
                <th>Buyer</th>
                <th className="num">Price</th>
                <th className="num">Rate</th>
                <th>Earned when</th>
                <th className="num">Commission</th>
                <th className="num">Earned so far</th>
              </tr>
            }
          >
            {sales.map((sale) => (
              <tr key={sale.purchaseId} data-sale={sale.reference}>
                <td>
                  <Link
                    className="font-medium text-brand-700 hover:underline"
                    to={`/app/development/sales/${sale.purchaseId}`}
                  >
                    {sale.reference}
                  </Link>
                  <div className="text-xs text-ink-500">
                    {sale.estateName}
                    {sale.plotLabel ? ` · ${sale.plotLabel}` : ""}
                  </div>
                </td>
                <td className="text-ink-600">{sale.buyerName}</td>
                <td className="num">{sale.priceLabel}</td>
                <td className="num">{commissionLabel(sale.commissionBp) ?? "—"}</td>
                <td className="text-ink-600">{sale.basisLabel}</td>
                <td className="num">{sale.fullLabel}</td>
                <td className="num font-medium">{sale.earnedLabel}</td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      {/* --------------------------------------------------------- the payments */}

      <Card padded={false}>
        <div className="border-b border-ink-200 p-4">
          <CardTitle hint="A payment is withdrawn by a stamp rather than deleted — the record of what was sent has to survive the correction, and withdrawing it is also what releases the money back into what is owed.">
            Payments made
          </CardTitle>
        </div>
        {!paid.data?.payments.length ? (
          <p className="p-6 text-center text-sm text-ink-500">Nothing has been paid yet.</p>
        ) : (
          <Table
            head={
              <tr>
                <th>Reference</th>
                <th>Date</th>
                <th>Method</th>
                <th className="num">Amount</th>
                <th />
              </tr>
            }
          >
            {paid.data.payments.map((payment) => (
              <tr key={payment.id} className={payment.reversedAt ? "text-ink-400" : undefined}>
                <td className="font-medium">
                  {payment.reference}
                  {payment.reversedAt ? (
                    <div className="text-xs text-danger-600">Withdrawn. {payment.reversalReason}</div>
                  ) : null}
                </td>
                <td>{payment.paidOn}</td>
                <td>{payment.method.replace("_", " ")}</td>
                <td className="num">{payment.amountLabel}</td>
                <td className="text-right">
                  {can("payout.record") && !payment.reversedAt ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={reverse.isPending}
                      onClick={() => {
                        const reason = window.prompt("Why is this being withdrawn? It stays on the record.");
                        if (reason) reverse.mutate({ paymentId: payment.id, reason });
                      }}
                    >
                      Withdraw
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </div>
  );
}

/** One payment the agency has actually made, withdrawn ones included and marked. */
interface Payment {
  id: string;
  reference: string;
  paidOn: string;
  method: string;
  amount: number;
  amountLabel: string;
  reversedAt: string | null;
  reversalReason: string | null;
  notes: string | null;
}

/* -------------------------------------------------------- the printable page */

/**
 * The statement, as a document.
 *
 * Same letterhead as the buyer's statement and the offer letter, for the same reason: a marketer
 * being sent a figure has to be able to see whose figure it is. `printable` puts it alone on the
 * sheet — the rail, the bar and every button carry `no-print`.
 *
 * Every line is checkable from the other side: an accrual names the receipt the *buyer* holds, so a
 * marketer who doubts a figure can ask about a specific payment rather than about the total.
 */
export function MarketerStatementPage() {
  const { id = "" } = useParams();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["marketer-statement", id],
    queryFn: () =>
      api.get<{
        marketer: MarketerRow;
        statement: MarketerStatement;
        problems: Problem[];
        payments: Payment[];
        agency: Agency;
        asAt: string;
      }>(`/marketers/${id}/statement`),
  });

  if (isLoading) return <Spinner />;
  if (error || !data) return <ErrorState error={error} retry={() => refetch()} />;

  const { marketer, statement, payments, agency, problems } = data;

  return (
    <div className="max-w-4xl">
      <PageHeader
        title="Commission statement"
        back={{ to: `/app/development/marketers/${id}`, label: marketer.name }}
        description="What has been earned, what has been paid, and what is outstanding. It can be sent or printed."
        actions={
          <Button variant="secondary" className="no-print" onClick={() => window.print()}>
            Print
          </Button>
        }
      />

      {problems.length ? (
        <div className="mb-4 no-print">
          <Alert tone="danger" title="This account does not reconcile.">
            The figures below do not agree with their own lines, and nothing may be paid from it until they do.
          </Alert>
        </div>
      ) : null}

      <Card padded={false} className="printable">
        <div className="border-b border-ink-200 p-6">
          <div className="flex flex-wrap items-start gap-4">
            <AgencyMark name={agency.name} logoUrl={agency.logoUrl}>
              {agency.address ? <p className="text-sm text-ink-500">{agency.address}</p> : null}
              <p className="text-sm text-ink-500">{[agency.phone, agency.email].filter(Boolean).join(" · ")}</p>
            </AgencyMark>
            <div className="ml-auto text-right">
              <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Commission statement</p>
              <p className="font-medium">{marketer.name}</p>
              <p className="text-sm text-ink-500">as at {data.asAt}</p>
            </div>
          </div>
        </div>

        <div className="grid gap-4 border-b border-ink-200 p-6 sm:grid-cols-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Earned</p>
            <p className="mt-1 text-xl font-semibold">{statement.earnedLabel}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Paid</p>
            <p className="mt-1 text-xl font-semibold">{statement.paidLabel}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Outstanding</p>
            <p className={`mt-1 text-xl font-semibold ${statement.owed < 0 ? "text-danger-600" : ""}`}>
              {statement.owedLabel}
            </p>
          </div>
        </div>

        <Table
          head={
            <tr>
              <th>Date</th>
              <th>What it is</th>
              <th className="num">Drawn on</th>
              <th className="num">Rate</th>
              <th className="num">Commission</th>
            </tr>
          }
        >
          {/*
            Keyed by position as well as by source.

            Two lines of a statement can be identical in every visible respect — one receipt
            allocated across two instalments of the same plan on the same day earns the same
            commission twice — so anything built out of what is shown collides. The index is what
            distinguishes them, and the ordering is the server's and stable.
          */}
          {statement.lines.map((line, index) => (
            <tr key={`${line.sourceKind}-${line.sourceId}-${index}`}>
              <td className="text-ink-600">{line.date}</td>
              <td>
                <span className="font-medium">{line.description}</span>
                <div className="text-xs text-ink-500">
                  {line.estateName}
                  {line.plotLabel ? ` · ${line.plotLabel}` : ""}
                </div>
              </td>
              <td className="num text-ink-600">{line.grossLabel}</td>
              <td className="num text-ink-600">{commissionLabel(line.bp) ?? "—"}</td>
              <td className="num font-medium">{line.commissionLabel}</td>
            </tr>
          ))}
        </Table>

        {statement.lines.length === 0 ? (
          <p className="p-6 text-center text-sm text-ink-500">Nothing has been earned yet.</p>
        ) : null}

        <div className="border-t border-ink-200 p-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Payments made</p>
          {!payments.length ? (
            <p className="mt-2 text-sm text-ink-500">Nothing has been paid yet.</p>
          ) : (
            <ul className="mt-2 space-y-1 text-sm">
              {payments.map((payment) => (
                <li key={payment.id} className={payment.reversedAt ? "text-ink-400 line-through" : ""}>
                  <span className="font-medium">{payment.paidOn}</span> — {payment.reference}, {payment.amountLabel}
                  {payment.reversedAt ? " (withdrawn)" : ""}
                </li>
              ))}
            </ul>
          )}
          <Copyright className="mt-4" />
        </div>
      </Card>
    </div>
  );
}

/* --------------------------------------------------------------- the picker */

/**
 * Who sold it, on a form that records a sale.
 *
 * **Leaving it empty is the ordinary case.** Plenty of sales are made by the developer's own office
 * with nobody on commission, so this offers "nobody" first and asks for nothing else — the rate and
 * the basis come off the tree by themselves, and the two override boxes beside this exist for the
 * one deal that was done differently rather than for every deal.
 *
 * Only active and invited people are offered. Somebody marked inactive has stopped selling, and a
 * picker that keeps offering them is how a sale ends up on the wrong statement; they stay on their
 * existing sales, which is the whole reason marking beats deleting.
 */
export function MarketerPicker({
  value,
  onChange,
  id = "marketerId",
}: {
  value: string;
  onChange: (next: string) => void;
  id?: string;
}) {
  const { data } = useQuery({
    queryKey: ["marketers", ""],
    queryFn: () => api.get<{ marketers: MarketerRow[] }>("/marketers"),
    /* A member who may record a sale but not read the commission book gets a refusal here. The
       picker then offers nobody, which is exactly right: they are not the person who decides. */
    retry: false,
    staleTime: 60_000,
  });

  const choices = (data?.marketers ?? []).filter((row) => row.status !== "inactive" || row.id === value);

  return (
    <Select id={id} name="marketerId" value={value} onChange={(event) => onChange(event.target.value)}>
      <option value="">Nobody — no commission on this sale</option>
      {choices.map((marketer) => (
        <option key={marketer.id} value={marketer.id}>
          {marketer.name}
        </option>
      ))}
    </Select>
  );
}

/**
 * The two overrides that sit beside the picker, and the sentence that says why they are usually
 * left alone.
 *
 * Drawn only once somebody has chosen a marketer, because a rate with nobody to earn it is a
 * number that goes nowhere. Both are optional: empty means the tree decides, which is what makes
 * the price list and the rate chain worth maintaining in the first place.
 */
export function CommissionOverrides({
  bp,
  basis,
  onBp,
  onBasis,
}: {
  bp: string;
  basis: string;
  onBp: (next: string) => void;
  onBasis: (next: string) => void;
}) {
  return (
    <>
      <Field
        label="Commission rate"
        htmlFor="commissionBp"
        hint="Per cent. Leave it empty to take the rate from the property, the release or the price list."
      >
        <Input
          id="commissionBp"
          type="number"
          min={0}
          max={100}
          step="0.01"
          value={bp}
          onChange={(event) => onBp(event.target.value)}
          placeholder="2.5"
        />
      </Field>
      <Field label="Earned when" htmlFor="commissionBasis" hint="Leave it as the property's unless this deal was agreed differently.">
        <Select id="commissionBasis" value={basis} onChange={(event) => onBasis(event.target.value)}>
          <option value="">As the property says</option>
          {Object.values(COMMISSION_BASIS_INFO).map((info) => (
            <option key={info.value} value={info.value}>
              {info.label}
            </option>
          ))}
        </Select>
      </Field>
    </>
  );
}
